import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useSC, Card, Kpi, Table, Td, Pager, Pill, SCEN_TONE, money, num, fmtDate, monthLabel, Loading } from "./parts";

export function DietitiansTab({ onOpenDietitian }) {
  const { data } = useSC("/dietitians");
  if (!data) return <Loading />;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.map((d) => (
        <button key={d.id} data-testid={`sc-dietitian-${d.id}`} onClick={() => onOpenDietitian(d.id)} className="card-soft p-5 text-left hover:-translate-y-0.5 transition-transform">
          <div className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{d.id} · ★ {d.rating}</div>
          <div className="font-display text-xl" style={{ color: "var(--primary)" }}>{d.name}</div>
          <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>{d.title}</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="font-bold">{num(d.total)}</div><div className="text-[10px] uppercase opacity-60">Clients</div></div>
            <div><div className="font-bold">{num(d.pending_plans)}</div><div className="text-[10px] uppercase opacity-60">Pending</div></div>
            <div><div className="font-bold" style={{ color: "var(--red)" }}>{money(d.revenue_fy)}</div><div className="text-[10px] uppercase opacity-60">FY revenue</div></div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function PlansTab({ onOpenPlan }) {
  const { data } = useSC("/plans");
  if (!data) return <Loading />;
  return (
    <Card title="Diet-plan assignments (from your real templates, read-only)">
      <Table head={["Template", "Assigned clients", "Delivered", "Pending", "Due for revision"]}>
        {data.map((p) => (
          <tr key={p.id} data-testid={`sc-plan-${p.id}`} onClick={() => onOpenPlan(p.id, p.name)} className="cursor-pointer hover:bg-[var(--bg)]">
            <Td className="font-semibold">{p.name}</Td><Td>{num(p.total)}</Td><Td>{num(p.delivered)}</Td><Td>{num(p.pending)}</Td><Td>{num(p.due_revision)}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

export function YogaTab() {
  const { data } = useSC("/yoga");
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        {data.coaches.map((c) => (
          <Card key={c.id} title={c.name} right={<span className="text-xs font-bold">★ {c.rating}</span>}>
            <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{c.style} · {c.batches.length} batches</div>
            <div className="text-sm mt-1"><b>{num(c.members)}</b> members · <b>{c.attendance_pct}%</b> attendance</div>
          </Card>
        ))}
      </div>
      <Card title="Active yoga batches">
        <Table head={["Batch", "Coach", "Style", "Time", "Days", "Mode", "Members"]}>
          {data.batches.map((b) => <tr key={b.id}><Td className="font-mono text-xs">{b.id}</Td><Td>{b.coach}</Td><Td>{b.style}</Td><Td>{b.slot}</Td><Td>{b.days}</Td><Td>{b.mode}</Td><Td className="font-bold">{num(b.members)}</Td></tr>)}
        </Table>
      </Card>
    </div>
  );
}

export function AppointmentsTab({ onOpenClient }) {
  const [view, setView] = useState("upcoming");
  const [page, setPage] = useState(1);
  const { data } = useSC(`/appointments?view=${view}&page=${page}`);
  const views = [["upcoming", "Upcoming (14 days)"], ["completed", "Completed (last 14 days)"], ["overdue", "Overdue follow-ups"]];
  return (
    <Card title="Appointments & consultations" right={
      <div className="flex gap-1">{views.map(([k, l]) => <button key={k} data-testid={`sc-appt-${k}`} onClick={() => { setView(k); setPage(1); }} className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: view === k ? "var(--primary)" : "transparent", color: view === k ? "var(--yellow)" : "var(--primary)", border: "1.5px solid var(--primary)" }}>{l}</button>)}</div>}>
      {!data ? <Loading /> : (
        <>
          <Table head={["Date", "Client", "Type", "Dietitian", "Goal", "Plan status"]}>
            {data.items.map((a) => (
              <tr key={a.cid} onClick={() => onOpenClient(a.cid)} className="cursor-pointer hover:bg-[var(--bg)]">
                <Td>{fmtDate(a.date)}</Td><Td className="font-semibold">{a.name} <span className="font-mono text-xs opacity-60">{a.cid}</span></Td><Td>{a.type}</Td><Td>{a.dietitian}</Td><Td>{a.goal}</Td><Td><Pill tone={SCEN_TONE[a.scenario]}>{a.plan_status}</Pill></Td>
              </tr>
            ))}
          </Table>
          <Pager data={data} page={page} setPage={setPage} testid="sc-appt" />
        </>
      )}
    </Card>
  );
}

export function SalesTab({ onOpenClient }) {
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useSC(`/sales?stage=${encodeURIComponent(stage)}&page=${page}`);
  if (!data) return <Loading />;
  const stages = ["", "New", "Contacted", "Consult booked", "Proposal sent", "Won", "Lost"];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {stages.slice(1).map((s) => <Kpi key={s} label={s} value={num(data.stages[s])} />)}
      </div>
      <Card title="Sales team performance (this FY)">
        <div className="h-56"><ResponsiveContainer><BarChart data={data.reps}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(20,20,43,0.08)" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
          <Bar dataKey="leads" name="Leads handled" fill="#14142B" radius={[4, 4, 0, 0]} /><Bar dataKey="won" name="Won" fill="#B93B3B" radius={[4, 4, 0, 0]} />
        </BarChart></ResponsiveContainer></div>
      </Card>
      <Card title="Leads (last 90 days)" right={
        <select data-testid="sc-lead-stage" value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }} className="text-xs px-3 py-1 rounded-full border bg-transparent" style={{ borderColor: "var(--border-strong)" }}>
          {stages.map((s) => <option key={s} value={s}>{s || "All stages"}</option>)}
        </select>}>
        <Table head={["Lead", "Name", "City", "Source", "Goal", "Sales rep", "Created", "Stage"]}>
          {data.leads.items.map((l) => (
            <tr key={l.id} onClick={() => l.cid && onOpenClient(l.cid)} className={l.cid ? "cursor-pointer hover:bg-[var(--bg)]" : ""}>
              <Td className="font-mono text-xs">{l.id}</Td><Td className="font-semibold">{l.name}</Td><Td>{l.city}</Td><Td>{l.source}</Td><Td>{l.goal}</Td><Td>{l.rep}</Td><Td>{fmtDate(l.created)}</Td>
              <Td><Pill tone={l.stage === "Won" ? "green" : l.stage === "Lost" ? "red" : "yellow"}>{l.stage}{l.cid ? ` · ${l.cid}` : ""}</Pill></Td>
            </tr>
          ))}
        </Table>
        <Pager data={data.leads} page={page} setPage={setPage} testid="sc-leads" />
      </Card>
    </div>
  );
}

export function RevenueTab({ onOpenClient }) {
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useSC(`/revenue?month=${month}&page=${page}`);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <Card title="New vs renewal revenue by month (click a bar to see its transactions)">
        <div className="h-64"><ResponsiveContainer><BarChart data={data.monthly}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(20,20,43,0.08)" /><XAxis dataKey="month" tickFormatter={monthLabel} fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
          <Tooltip formatter={(v) => money(v)} labelFormatter={monthLabel} /><Legend />
          <Bar dataKey="new" name="New customers" stackId="a" fill="#B93B3B" cursor="pointer" onClick={(x) => { setMonth(x.month); setPage(1); }} />
          <Bar dataKey="renewal" name="Renewals" stackId="a" fill="#14142B" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(x) => { setMonth(x.month); setPage(1); }} />
        </BarChart></ResponsiveContainer></div>
      </Card>
      <Card title={`Transactions ${month ? `· ${monthLabel(month)}` : "· this FY"} · ${money(data.tx_total)}`} right={month && <button data-testid="sc-rev-clear" onClick={() => { setMonth(""); setPage(1); }} className="text-xs font-bold underline">Show all</button>}>
        <Table head={["Txn", "Date", "Client", "Dietitian", "Package", "Type", "Amount"]}>
          {data.transactions.items.map((t) => (
            <tr key={t.id} onClick={() => onOpenClient(t.cid)} className="cursor-pointer hover:bg-[var(--bg)]">
              <Td className="font-mono text-xs">{t.id}</Td><Td>{fmtDate(t.date)}</Td><Td className="font-semibold">{t.client}</Td><Td>{t.dietitian}</Td><Td>{t.package}</Td><Td className="capitalize">{t.type}</Td><Td className="font-bold">{money(t.amount)}</Td>
            </tr>
          ))}
        </Table>
        <Pager data={data.transactions} page={page} setPage={setPage} testid="sc-tx" />
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Subscription packages">
          <Table head={["Package", "Duration", "List price"]}>{data.packages.map((p) => <tr key={p.name}><Td>{p.name}</Td><Td>{p.months} mo</Td><Td>{money(p.price)}</Td></tr>)}</Table>
        </Card>
        <Card title={`Financial report · ${data.previous_fy.label} (historical)`}>
          <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>{money(data.previous_fy.total)}</div>
          <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>{data.previous_fy.note}</p>
          <div className="h-40"><ResponsiveContainer><BarChart data={data.previous_fy.monthly}>
            <XAxis dataKey="month" tickFormatter={monthLabel} fontSize={10} /><YAxis fontSize={10} tickFormatter={(v) => `$${v / 1000}k`} /><Tooltip formatter={(v) => money(v)} labelFormatter={monthLabel} />
            <Bar dataKey="revenue" fill="#8B6D00" radius={[3, 3, 0, 0]} />
          </BarChart></ResponsiveContainer></div>
        </Card>
      </div>
    </div>
  );
}

export function StaffTab({ onOpenDietitian }) {
  const { data } = useSC("/staff");
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <Card title="Dietitians">
        <Table head={["Name", "Clients", "Active", "Plans delivered", "Upcoming consults", "Renewals FY", "Revenue FY", "Rating"]}>
          {data.dietitians.map((d) => <tr key={d.id} onClick={() => onOpenDietitian(d.id)} className="cursor-pointer hover:bg-[var(--bg)]"><Td className="font-semibold">{d.name}</Td><Td>{num(d.total)}</Td><Td>{num(d.active)}</Td><Td>{num(d.delivered_plans)}</Td><Td>{num(d.upcoming_consults)}</Td><Td>{num(d.renewals_fy)}</Td><Td className="font-bold">{money(d.revenue_fy)}</Td><Td>★ {d.rating}</Td></tr>)}
        </Table>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Yoga coaches">
          <Table head={["Name", "Batches", "Members", "Attendance"]}>{data.coaches.map((c) => <tr key={c.id}><Td className="font-semibold">{c.name}</Td><Td>{c.batches.length}</Td><Td>{num(c.members)}</Td><Td>{c.attendance_pct}%</Td></tr>)}</Table>
        </Card>
        <Card title="Sales team">
          <Table head={["Name", "Leads", "Won", "Conversion", "New revenue FY"]}>{data.sales.map((s) => <tr key={s.id}><Td className="font-semibold">{s.name}</Td><Td>{num(s.leads)}</Td><Td>{num(s.won)}</Td><Td>{s.conversion_pct}%</Td><Td className="font-bold">{money(s.new_revenue_fy)}</Td></tr>)}</Table>
        </Card>
      </div>
    </div>
  );
}

export function ActivityTab() {
  const { data } = useSC("/activity");
  if (!data) return <Loading />;
  const tone = { warn: "yellow", alert: "red", info: "blue" };
  const kind = { payment: "green", join: "blue", plan: "yellow" };
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Card title="Notifications">{data.notifications.map((n, i) => <div key={i} className="py-2 border-t first:border-t-0 text-sm flex gap-2 items-start" style={{ borderColor: "var(--border-subtle)" }}><Pill tone={tone[n.level]}>{n.level}</Pill><span>{n.text}</span></div>)}</Card>
      <Card title="Activity log" className="md:col-span-2">
        {data.events.map((e, i) => <div key={i} className="py-2 border-t first:border-t-0 text-sm flex gap-3 items-start" style={{ borderColor: "var(--border-subtle)" }}><span className="text-xs w-24 shrink-0" style={{ color: "var(--text-secondary)" }}>{fmtDate(e.date)}</span><Pill tone={kind[e.kind]}>{e.kind}</Pill><span>{e.text}</span></div>)}
      </Card>
    </div>
  );
}
